import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { useOutletContext, useNavigate } from 'react-router-dom';
import usePermissions from '../../auth/usePermissions';
import { ChevronsUpDown, ChevronUp, ChevronDown, Plus, Loader2, MoreVertical } from 'lucide-react';
import { authFetch } from '../../components/utils/authFetch';
import '../../component-styles/RecordPages.css';
import QuickBooksConnectModal from '../../components/utils/QuickbooksConnectModal';
import { isQuickBooksConnectionError } from '../../utils/quickbooksErrors';
import SendEmailModal from '../../components/common/SendEmailModal';
import InvoiceFolioModal from '../../components/Projects/Invoices/InvoiceFolioModal';
import ClientViewedBadge from '../../components/shared/ClientViewedBadge';
import RecordPagination from '../../components/shared/RecordPagination';
import ConfirmModal from '../../components/utils/ConfirmModal';
import API_BASE from '../../utils/api.js';
import { unassignedItemsNotice } from '../../utils/convertToPoMessage.js';
import notify from '../../utils/notify';
import useProjects from '../../hooks/useProjects';
import usePageSize from '../../hooks/usePageSize';

/* ---------------------- Helpers ---------------------- */
function getApiBase() {
  return API_BASE;
}

function formatCurrency(v) {
  if (v == null || Number.isNaN(Number(v))) return '$0.00';
  return Number(v).toLocaleString(undefined, {
    style: 'currency',
    currency: 'USD',
  });
}

function getInvoiceTotal(inv) {
  return Number(inv?.totals?.grandTotal ?? inv?.totals?.total ?? 0);
}

function getInvoiceRemaining(inv) {
  const total = getInvoiceTotal(inv);
  if (inv?.recordType === 'timebilling') {
    return Math.max(0, Number(inv?.remainingBalance ?? total));
  }
  const paid = Number(inv?.totals?.paid ?? 0);
  return Math.max(0, Number(inv?.remainingBalance ?? total - paid));
}

function getInvoiceStatus(inv) {
  if (inv?.recordType === 'timebilling') {
    const total = getInvoiceTotal(inv);
    const remaining = Number(inv?.remainingBalance ?? total);
    if (remaining <= 0) return 'paid';
    return Array.isArray(inv?.paymentHistory) && inv.paymentHistory.length > 0
      ? 'partial'
      : 'unpaid';
  }
  return inv?.status || 'draft';
}

function SortIcon({ active, order }) {
  if (!active) return <ChevronsUpDown size={14} className="sort-icon neutral" />;
  return order === 'asc' ? (
    <ChevronUp size={14} className="sort-icon active" />
  ) : (
    <ChevronDown size={14} className="sort-icon active" />
  );
}

function getQuickBooksInvoiceUrl(invoiceId, env = 'sandbox') {
  const base = env === 'production' ? 'https://qbo.intuit.com' : 'https://sandbox.qbo.intuit.com';

  return `${base}/app/invoice?txnId=${invoiceId}`;
}

function DropdownMenu({
  shareToken,
  onSendToClient,
  onPublic,
  onCreateQBInvoice,
  onDuplicate,
  onDelete,
  onConvertToPO,
  canDelete,
  qbInvoiceId,
  qbEnvironment,
  canSend,
  canEditCreate,
}) {
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="menu-container" ref={menuRef}>
      <button
        className="menu-toggle"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((prev) => !prev);
        }}
      >
        <MoreVertical size={16} />
      </button>

      <div className={`menu-dropdown ${open ? 'open' : ''}`}>
        {canSend !== false && <button onClick={onSendToClient}>Send to Client</button>}
        <button onClick={onPublic}>View Public Link</button>

        {!qbInvoiceId && <button onClick={onCreateQBInvoice}>Create QuickBooks Invoice</button>}

        {qbInvoiceId && (
          <button
            onClick={() =>
              window.open(
                getQuickBooksInvoiceUrl(qbInvoiceId, qbEnvironment || 'sandbox'),
                '_blank'
              )
            }
          >
            View in QuickBooks
          </button>
        )}

        <button onClick={onConvertToPO}>Convert to Purchase Order</button>
        {/* <button disabled>Send Reminder</button> */}
        {canEditCreate !== false && <button onClick={onDuplicate}>Duplicate Invoice</button>}
        {canDelete && canEditCreate !== false && (
          <button
            className="menu-danger"
            onClick={() => {
              setOpen(false);
              onDelete?.();
            }}
          >
            Delete Invoice
          </button>
        )}
      </div>
    </div>
  );
}

function TBDropdownMenu({ onDelete, onSendToClient, canSend }) {
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="menu-container" ref={menuRef}>
      <button
        className="menu-toggle"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((prev) => !prev);
        }}
      >
        <MoreVertical size={16} />
      </button>

      <div className={`menu-dropdown ${open ? 'open' : ''}`}>
        {canSend !== false && (
          <button onClick={() => { setOpen(false); onSendToClient?.(); }}>
            Send to Client
          </button>
        )}
        <button
          className="menu-danger"
          onClick={() => {
            setOpen(false);
            onDelete?.();
          }}
        >
          Delete Invoice
        </button>
      </div>
    </div>
  );
}

/* ---------------------- Main Component ---------------------- */
export default function InvoicesSection({ projectId: propProjectId, project: propProject }) {
  const outlet = useOutletContext?.() || {};
  const projectId = propProjectId || outlet.projectId || '';
  const project = propProject || outlet.project || null;
  const navigate = useNavigate();

  const { permissions } = usePermissions();
  const canCreateInvoices = permissions.createEditInvoicesProposals !== false;
  const canSendLinks = permissions.sendInvoiceProposalLinks !== false;

  const API_BASE = getApiBase();
  const userId = localStorage.getItem('userId');

  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [sortField, setSortField] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = usePageSize('invoices');

  useEffect(() => { setCurrentPage(1); }, [searchQuery]);

  const [showQBModal, setShowQBModal] = useState(false);
  // The send that hit "not connected", replayed once QuickBooks is reconnected.
  const qbRetryRef = useRef(null);
  const [showFolio, setShowFolio] = useState(null);
  const [vendorModal, setVendorModal] = useState({ open: false, invoice: null, vendors: [], itemIds: [], unassignedCount: 0 });
  const [itemSelectModal, setItemSelectModal] = useState({ open: false, invoice: null, items: [], selected: [] });
  const [confirmModal, setConfirmModal] = useState({ open: false, title: '', message: '', confirmLabel: 'Confirm', destructive: false, pendingAction: null });
  const [sendInvoiceModal, setSendInvoiceModal] = useState({ open: false, inv: null, publicUrl: '', email: '' });
  const [sendTBInvoiceModal, setSendTBInvoiceModal] = useState({ open: false, inv: null, publicUrl: '', email: '' });
  const [duplicateModal, setDuplicateModal] = useState({ open: false, inv: null, targetProjectId: '' });
  const { projects: duplicateTargetProjects } = useProjects({ activeOnly: true, isOpen: duplicateModal.open });

  function askConfirm({ title, message, confirmLabel, destructive, action }) {
    setConfirmModal({ open: true, title, message, confirmLabel, destructive: !!destructive, pendingAction: action });
  }

  async function handleConfirm() {
    const action = confirmModal.pendingAction;
    setConfirmModal((s) => ({ ...s, open: false, pendingAction: null }));
    if (action) await action();
  }

  function handleCancel() {
    setConfirmModal((s) => ({ ...s, open: false, pendingAction: null }));
  }

  /* ---------------------- Fetch Invoices ---------------------- */
  const load = useCallback(
    async (isBackground = false) => {
      if (!userId || !projectId) return;
      if (isBackground) setIsRefreshing(true);
      else setLoading(true);

      try {
        const [invoiceRes, tbInvoiceRes] = await Promise.all([
          fetch(
            `${API_BASE}/api/invoices/${encodeURIComponent(userId)}/${encodeURIComponent(projectId)}`
          ),
          fetch(
            `${API_BASE}/api/timebilling-invoices/project/${encodeURIComponent(
              userId
            )}/${encodeURIComponent(projectId)}`
          ),
        ]);

        if (!invoiceRes.ok) throw new Error(`Invoice server returned ${invoiceRes.status}`);
        if (!tbInvoiceRes.ok)
          throw new Error(`Time billing server returned ${tbInvoiceRes.status}`);

        const [invoiceData, tbInvoiceData] = await Promise.all([
          invoiceRes.json(),
          tbInvoiceRes.json(),
        ]);

        const projectInvoices = (Array.isArray(invoiceData) ? invoiceData : []).map((inv) => ({
          ...inv,
          recordType: 'invoice',
        }));
        const timeBillingInvoices = (Array.isArray(tbInvoiceData) ? tbInvoiceData : []).map(
          (inv) => ({
            ...inv,
            recordType: 'timebilling',
          })
        );

        setInvoices([...projectInvoices, ...timeBillingInvoices]);
      } catch (err) {
        console.error('Failed to load invoices', err);
        setInvoices([]);
      } finally {
        if (isBackground) setIsRefreshing(false);
        else setLoading(false);
      }
    },
    [API_BASE, userId, projectId]
  );

  useEffect(() => {
    load();
    const interval = setInterval(() => load(true), 30000);
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') load(true);
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [load]);

  /* ---------------------- Totals ---------------------- */
  const totals = useMemo(() => {
    const grandTotal = invoices.reduce((s, inv) => s + getInvoiceTotal(inv), 0);
    const amountDue = invoices.reduce((s, inv) => s + getInvoiceRemaining(inv), 0);
    const amountPaid = grandTotal - amountDue;
    return { grandTotal, amountDue, amountPaid };
  }, [invoices]);

  /* ---------------------- Sorting & Filtering ---------------------- */
  const filtered = useMemo(() => {
    const q = (searchQuery || '').trim().toLowerCase();
    let arr = invoices.filter((inv) => {
      if (!q) return true;
      return (
        (inv.invoiceNumber || '').toLowerCase().includes(q) ||
        (inv.title || '').toLowerCase().includes(q) ||
        (inv.client?.name || '').toLowerCase().includes(q) ||
        (getInvoiceStatus(inv) || '').toLowerCase().includes(q) ||
        (inv.recordType || '').toLowerCase().includes(q)
      );
    });

    arr.sort((a, b) => {
      const get = (it, field) => {
        if (field === 'date') return new Date(it.invoiceDate || it.createdAt || it.updatedAt).getTime();
        if (field === 'total') return getInvoiceRemaining(it);
        if (field === 'status') return getInvoiceStatus(it);
        return (it[field] || '').toString().toLowerCase();
      };
      const va = get(a, sortField);
      const vb = get(b, sortField);
      if (va === vb) return 0;
      if (sortOrder === 'asc') return va < vb ? -1 : 1;
      return va > vb ? -1 : 1;
    });

    return arr;
  }, [invoices, sortField, sortOrder, searchQuery]);

  const paginated = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  function toggleSort(field) {
    if (sortField === field) setSortOrder((s) => (s === 'asc' ? 'desc' : 'asc'));
    else {
      setSortField(field);
      setSortOrder('desc');
    }
    setCurrentPage(1);
  }

  /* ---------------------- Actions ---------------------- */
  async function openPublicLink(inv) {
    try {
      if (inv.shareToken) {
        const publicUrl = `${window.location.origin}/public/invoice/${inv.shareToken}`;
        window.open(publicUrl, '_blank');
        return;
      }

      const res = await fetch(
        `${API_BASE}/api/invoices/${encodeURIComponent(userId)}/${encodeURIComponent(
          projectId
        )}/${encodeURIComponent(inv.invoiceId)}`
      );
      if (!res.ok) throw new Error(`Server returned ${res.status}`);

      const fresh = await res.json();
      if (fresh.shareToken) {
        const publicUrl = `${window.location.origin}/public/invoice/${fresh.shareToken}`;
        window.open(publicUrl, '_blank');
        return;
      }

      throw new Error('No shareToken found');
    } catch (e) {
      console.error('Failed to open public invoice link', e);
      notify('Failed to generate public invoice link.');
    }
  }

  async function openSendToClientModal(inv) {
    try {
      let current = inv;
      if (!current.shareToken) {
        const res = await fetch(
          `${API_BASE}/api/invoices/${encodeURIComponent(userId)}/${encodeURIComponent(
            projectId
          )}/${encodeURIComponent(inv.invoiceId)}`
        );
        if (!res.ok) throw new Error(`Server returned ${res.status}`);
        current = await res.json();
        if (!current.shareToken) throw new Error('No shareToken found');
      }

      // Sending to the client should also mark the invoice as sent — otherwise it
      // stays in "draft" status and the client portal hides it from the client.
      if (current.status === 'draft') {
        const putRes = await authFetch(
          `${API_BASE}/api/invoices/${encodeURIComponent(userId)}/${encodeURIComponent(
            projectId
          )}/${encodeURIComponent(current.invoiceId)}`,
          {
            method: 'PUT',
            body: JSON.stringify({ ...current, status: 'sent', sentAt: new Date().toISOString() }),
          }
        );
        if (putRes.ok) {
          current = await putRes.json();
          setInvoices((prev) =>
            prev.map((i) => (i.invoiceId === current.invoiceId ? { ...current, recordType: 'invoice' } : i))
          );
        }
      }

      const publicUrl = `${window.location.origin}/public/invoice/${current.shareToken}`;
      const primaryEmail = current.client?.email || project?.email || project?.customerEmail || '';
      const email = [...new Set([primaryEmail, project?.secondaryEmail].filter(Boolean))].join(', ');
      setSendInvoiceModal({ open: true, inv: current, publicUrl, email, sending: false });
    } catch (e) {
      console.error('Failed to get public invoice link', e);
      notify('Failed to generate public invoice link.');
    }
  }

  async function openSendTBToClientModal(inv) {
    try {
      let shareToken = inv.shareToken;
      if (!shareToken) {
        const res = await authFetch(
          `${API_BASE}/api/timebilling-invoices/${encodeURIComponent(userId)}/${encodeURIComponent(inv.tbInvoiceId)}/share`,
          { method: 'POST' }
        );
        if (!res.ok) throw new Error(`Server returned ${res.status}`);
        const data = await res.json();
        shareToken = data.shareToken;
      }
      if (!shareToken) throw new Error('No shareToken returned');
      const publicUrl = `${window.location.origin}/public/timebilling-invoice/${shareToken}`;
      const primaryEmail = inv.billToEmail || project?.email || '';
      const email = [...new Set([primaryEmail, project?.secondaryEmail].filter(Boolean))].join(', ');
      setSendTBInvoiceModal({ open: true, inv, publicUrl, email });
    } catch (e) {
      console.error('Failed to get TB invoice share link', e);
      notify('Failed to generate invoice link.');
    }
  }

  async function createQBInvoice(inv) {
    askConfirm({
      title: 'Send to QuickBooks',
      message: 'Send this invoice to QuickBooks? This will create a new invoice record in your QuickBooks account.',
      confirmLabel: 'Send to QuickBooks',
      action: async () => {
        try {
          const res = await authFetch(`${API_BASE}/api/quickbooks/create-invoice`, {
            method: 'POST',
            body: JSON.stringify({
              invoiceId: inv.invoiceId,
              projectId,
            }),
          });

          if (!res.ok) {
            const data = await res.json().catch(() => ({}));
            if (isQuickBooksConnectionError(data)) {
              qbRetryRef.current = () => createQBInvoice(inv);
              setShowQBModal(true);
              return;
            }
            console.error('QB error response:', data);
            notify(data.error || 'Failed to create QuickBooks invoice.');
            return;
          }

          await res.json().catch(() => ({}));

          notify('QuickBooks invoice created successfully.');
          load(true);
        } catch (err) {
          console.error('Failed to create QB invoice', err);
          notify('Unable to connect to QuickBooks.');
        }
      },
    });
  }

  function duplicateInvoice(inv) {
    setDuplicateModal({ open: true, inv, targetProjectId: projectId });
  }

  async function confirmDuplicateInvoice() {
    const { inv, targetProjectId } = duplicateModal;
    if (!inv) return;
    setDuplicateModal({ open: false, inv: null, targetProjectId: '' });
    try {
      const res = await authFetch(
        `${API_BASE}/api/invoices/${encodeURIComponent(userId)}/${encodeURIComponent(
          projectId
        )}/${encodeURIComponent(inv.invoiceId)}/duplicate`,
        {
          method: 'POST',
          body: JSON.stringify({ targetProjectId: targetProjectId || projectId }),
        }
      );

      if (!res.ok) {
        const text = await res.text();
        console.error('Duplicate failed:', text);
        notify('Failed to duplicate invoice.');
        return;
      }

      const newInvoice = await res.json();
      const destProjectId = targetProjectId || projectId;

      navigate(`/projects/${destProjectId}/invoices/${newInvoice.invoiceId}`, {
        state: destProjectId === projectId ? { project, invoice: newInvoice } : { invoice: newInvoice },
      });
      notify(
        destProjectId === projectId
          ? 'Invoice duplicated as a new draft.'
          : 'Invoice duplicated to the selected project.'
      );
    } catch (err) {
      console.error('Duplicate invoice error', err);
      notify('Unable to duplicate invoice.');
    }
  }

  async function convertToPO(inv) {
    if (!inv?.invoiceId || !userId || !projectId) return;

    try {
      const res = await fetch(
        `${API_BASE}/api/invoices/${encodeURIComponent(userId)}/${encodeURIComponent(
          projectId
        )}/${encodeURIComponent(inv.invoiceId)}`
      );
      const data = await res.json();
      if (!res.ok) {
        notify(data.error || 'Failed to load invoice items.');
        return;
      }
      const items = Array.isArray(data.lineItems) ? data.lineItems : [];
      if (items.length === 0) {
        notify('This invoice has no line items to convert.');
        return;
      }
      setItemSelectModal({
        open: true,
        invoice: inv,
        items,
        selected: items.map((it) => it.itemId).filter(Boolean),
      });
    } catch (err) {
      console.error('Convert to PO error', err);
      notify('Unable to load invoice items.');
    }
  }

  function toggleConvertItemSelected(itemId) {
    setItemSelectModal((s) => ({
      ...s,
      selected: s.selected.includes(itemId)
        ? s.selected.filter((id) => id !== itemId)
        : [...s.selected, itemId],
    }));
  }

  function toggleConvertAllItemsSelected() {
    setItemSelectModal((s) => ({
      ...s,
      selected:
        s.selected.length === s.items.length
          ? []
          : s.items.map((it) => it.itemId).filter(Boolean),
    }));
  }

  async function proceedFromItemSelection() {
    const inv = itemSelectModal.invoice;
    const itemIds = itemSelectModal.selected;
    if (!inv || itemIds.length === 0) {
      notify('Select at least one item to convert.');
      return;
    }
    setItemSelectModal({ open: false, invoice: null, items: [], selected: [] });

    try {
      const preflight = await fetch(
        `${API_BASE}/api/invoices/${encodeURIComponent(userId)}/${encodeURIComponent(
          projectId
        )}/${encodeURIComponent(inv.invoiceId)}/convert-to-po`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ dryRun: true, itemIds }),
        }
      );
      const preflightData = await preflight.json();
      if (!preflight.ok) {
        notify(preflightData.error || 'Failed to check vendors.');
        return;
      }

      if (Array.isArray(preflightData.vendors) && preflightData.vendors.length > 1) {
        setVendorModal({
          open: true,
          invoice: inv,
          vendors: preflightData.vendors,
          itemIds,
          unassignedCount: preflightData.unassignedCount || 0,
        });
        return;
      }

      askConfirm({
        title: 'Create Purchase Order',
        message: `Create a purchase order from ${itemIds.length} selected item${
          itemIds.length === 1 ? '' : 's'
        }? A new draft PO will be generated from the selected line items.${unassignedItemsNotice(
          preflightData.unassignedCount
        )}`,
        confirmLabel: 'Create PO',
        action: async () => {
          try {
            const res = await fetch(
              `${API_BASE}/api/invoices/${encodeURIComponent(userId)}/${encodeURIComponent(
                projectId
              )}/${encodeURIComponent(inv.invoiceId)}/convert-to-po`,
              {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ splitByVendor: false, itemIds }),
              }
            );
            const data = await res.json();
            if (!res.ok) {
              notify(data.error || 'Failed to convert to purchase order.');
              return;
            }
            const poId = data.openedPoId || data.poId;
            if (poId) {
              navigate(`/projects/${projectId}/purchase-orders/${poId}`, { state: { project } });
            }
          } catch (err) {
            console.error('Convert to PO error', err);
            notify('Unable to convert to purchase order.');
          }
        },
      });
    } catch (err) {
      console.error('Convert to PO error', err);
      notify('Unable to convert to purchase order.');
    }
  }

  async function confirmSplitVendors() {
    const inv = vendorModal.invoice;
    const itemIds = vendorModal.itemIds || [];
    if (!inv) return;
    try {
      const res = await fetch(
        `${API_BASE}/api/invoices/${encodeURIComponent(userId)}/${encodeURIComponent(
          projectId
        )}/${encodeURIComponent(inv.invoiceId)}/convert-to-po`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ splitByVendor: true, itemIds }),
        }
      );
      const data = await res.json();
      if (!res.ok) {
        notify(data.error || 'Failed to convert to purchase order.');
        return;
      }
      const poId = data.openedPoId || data.poIds?.[0] || data.poId;
      if (poId) {
        navigate(`/projects/${projectId}/purchase-orders/${poId}`, { state: { project } });
      }
    } catch (err) {
      console.error('Convert to PO error', err);
      notify('Unable to convert to purchase order.');
    } finally {
      setVendorModal({ open: false, invoice: null, vendors: [], itemIds: [] });
    }
  }

  async function deleteTBInvoice(inv) {
    askConfirm({
      title: 'Delete Invoice',
      message: `Permanently delete time billing invoice ${inv.invoiceNumber || ''}? All time entries will be restored as unbilled.`,
      confirmLabel: 'Delete',
      destructive: true,
      action: async () => {
        try {
          const res = await fetch(
            `${API_BASE}/api/timebilling-invoices/${encodeURIComponent(userId)}/${encodeURIComponent(inv.tbInvoiceId)}`,
            { method: 'DELETE' }
          );
          const data = await res.json();
          if (!res.ok) {
            notify(data.error || 'Failed to delete invoice.');
            return;
          }
          load(true);
        } catch (err) {
          console.error('Delete TB invoice error', err);
          notify('Unable to delete invoice.');
        }
      },
    });
  }

  async function deleteInvoice(inv) {
    askConfirm({
      title: 'Delete Invoice',
      message: `Permanently delete invoice ${inv.invoiceNumber || ''}? This cannot be undone.`,
      confirmLabel: 'Delete',
      destructive: true,
      action: async () => {
        try {
          const res = await fetch(
            `${API_BASE}/api/invoices/${encodeURIComponent(userId)}/${encodeURIComponent(
              projectId
            )}/${encodeURIComponent(inv.invoiceId)}`,
            { method: 'DELETE' }
          );
          const data = await res.json();
          if (!res.ok) {
            notify(data.error || 'Failed to delete invoice.');
            return;
          }
          load(true);
        } catch (err) {
          console.error('Delete invoice error', err);
          notify('Unable to delete invoice.');
        }
      },
    });
  }

  /* ---------------------- JSX ---------------------- */
  return (
    <section className="record-section">
      <div className="record-top-row">
        <h2 className="record-title">Invoices</h2>

        <div className="record-controls">
          <input
            className="record-search"
            placeholder="Search invoice #, client, status..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {canCreateInvoices && (
            <button
              className="record-add-btn"
              onClick={() => navigate(`/projects/${projectId}/invoices/new`, { state: { project } })}
            >
              <Plus size={14} /> <span>Add New</span>
            </button>
          )}
        </div>
      </div>

      <div className="record-card">
        <div className="record-metrics">
          <div className="record-metric">
            <div className="record-metric-value">{formatCurrency(totals.grandTotal)}</div>
            <div className="record-metric-label">Total Amount</div>
          </div>
          <div className="record-metric">
            <div className="record-metric-value">{formatCurrency(totals.amountPaid)}</div>
            <div className="record-metric-label">Amount Paid</div>
          </div>
          <div className="record-metric">
            <div className="record-metric-value">{formatCurrency(totals.amountDue)}</div>
            <div className="record-metric-label">Amount Due</div>
          </div>
        </div>

        {isRefreshing && (
          <div className="record-refresh-banner">
            <Loader2 className="spin" size={14} style={{ marginRight: 6 }} />
            Refreshing Invoices…
          </div>
        )}

        <div className="record-table-wrap">
          {loading ? (
            <div className="record-empty">
              <div className="record-empty-icon" aria-hidden="true">
                ...
              </div>
              <div className="record-empty-title">Loading invoices…</div>
            </div>
          ) : filtered.length === 0 ? (
            <div className="record-empty">
              <div className="record-empty-icon" aria-hidden="true">
                [ ]
              </div>
              <div className="record-empty-title">No invoices yet</div>
              <div className="record-empty-sub">Create your first invoice for this project.</div>
              {canCreateInvoices && (
                <button
                  className="record-empty-cta"
                  onClick={() => navigate(`/projects/${projectId}/invoices/new`, { state: { project } })}
                >
                  <Plus size={14} /> Add New Invoice
                </button>
              )}
            </div>
          ) : (
            <table className="record-table" role="table" aria-label="Invoices">
              <thead>
                <tr>
                  <th onClick={() => toggleSort('date')}>
                    Date <SortIcon active={sortField === 'date'} order={sortOrder} />
                  </th>
                  <th>Type</th>
                  <th onClick={() => toggleSort('invoiceNumber')}>
                    Invoice # <SortIcon active={sortField === 'invoiceNumber'} order={sortOrder} />
                  </th>
                  <th>Client</th>
                  <th>Tags</th>
                  <th onClick={() => toggleSort('status')}>
                    Status <SortIcon active={sortField === 'status'} order={sortOrder} />
                  </th>
                  <th onClick={() => toggleSort('total')}>
                    Balance Due <SortIcon active={sortField === 'total'} order={sortOrder} />
                  </th>
                  <th>Folio</th>
                  <th>Actions</th>
                </tr>
              </thead>

              <tbody>
                {paginated.map((inv) => (
                  <tr key={`${inv.recordType}-${inv.invoiceId || inv.tbInvoiceId}`} className="record-row">
                    <td>{new Date(inv.invoiceDate || inv.createdAt || inv.updatedAt).toLocaleDateString()}</td>
                    <td>{inv.recordType === 'timebilling' ? 'Time Billing' : 'Invoice'}</td>
                    <td>
                      {inv.invoiceNumber || '—'}
                      {inv.title && <div className="record-doc-title">{inv.title}</div>}
                    </td>
                    <td>{inv.client?.name || inv.client?.email || inv.company?.name || '—'}</td>

                    {/* ✅ Tags */}
                    <td style={{ textAlign: 'left' }}>
                      {inv.recordType === 'invoice' &&
                      Array.isArray(inv.tags) &&
                      inv.tags.length > 0 ? (
                        <div className="tags-inline" title={inv.tags.join(', ')}>
                          {inv.tags.slice(0, 2).join(', ')}
                          {inv.tags.length > 2 ? '…' : ''}
                        </div>
                      ) : (
                        <span className="muted">—</span>
                      )}
                    </td>
                    {/* ✅ Status */}
                    <td>
                      <div className="record-status-cell">
                        <StatusBadge status={getInvoiceStatus(inv)} />
                        <ClientViewedBadge
                          firstViewedAt={inv.clientFirstViewedAt}
                          lastViewedAt={inv.clientLastViewedAt}
                          viewCount={inv.clientViewCount}
                          sent={getInvoiceStatus(inv) !== 'draft'}
                        />
                      </div>
                    </td>

                    {/* ✅ Total */}
                    <td>{formatCurrency(getInvoiceRemaining(inv))}</td>

                    <td>
                      {inv.recordType === 'invoice' ? (
                        <button
                          className="folio-icon-btn"
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowFolio(inv);
                          }}
                        >
                          <img
                            src="/folder-icon.png"
                            alt="Open folio"
                            className="link-icon"
                            width={18}
                            height={18}
                            draggable={false}
                          />
                        </button>
                      ) : (
                        <span className="muted">—</span>
                      )}
                    </td>

                    <td className="row-actions">
                      <div className="actions-wrapper">
                        <button
                          className="primary-action"
                          onClick={() =>
                            navigate(
                              inv.recordType === 'timebilling'
                                ? `/projects/${projectId}/timebilling-invoices/${inv.tbInvoiceId}`
                                : `/projects/${projectId}/invoices/${inv.invoiceId}`,
                              {
                                state: { project, invoice: inv },
                              }
                            )
                          }
                        >
                          View Invoice
                        </button>

                        {inv.recordType === 'invoice' && (
                          <DropdownMenu
                            shareToken={inv.shareToken}
                            qbInvoiceId={inv.quickbooks?.invoiceId}
                            qbEnvironment={inv.quickbooks?.environment}
                            onSendToClient={() => openSendToClientModal(inv)}
                            onPublic={() => openPublicLink(inv)}
                            onCreateQBInvoice={() => createQBInvoice(inv)}
                            onDuplicate={() => duplicateInvoice(inv)}
                            onConvertToPO={() => convertToPO(inv)}
                            onDelete={() => deleteInvoice(inv)}
                            canDelete={
                              Number(inv.totals?.paid || 0) <= 0 &&
                              !['paid', 'partial'].includes(String(inv.status || '').toLowerCase())
                            }
                            canSend={canSendLinks}
                            canEditCreate={canCreateInvoices}
                          />
                        )}

                        {inv.recordType === 'timebilling' && ['unpaid', 'partial'].includes(getInvoiceStatus(inv)) && canCreateInvoices && (
                          <TBDropdownMenu
                            onDelete={() => deleteTBInvoice(inv)}
                            onSendToClient={() => openSendTBToClientModal(inv)}
                            canSend={canSendLinks}
                          />
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <RecordPagination
          page={currentPage}
          pageSize={pageSize}
          total={filtered.length}
          onPageChange={setCurrentPage}
          onPageSizeChange={setPageSize}
        />
      </div>

      {showFolio && (
        <InvoiceFolioModal
          invoice={showFolio}
          projectId={projectId}
          onClose={() => setShowFolio(null)}
        />
      )}

      <QuickBooksConnectModal
        open={showQBModal}
        onClose={() => setShowQBModal(false)}
        onConnected={() => qbRetryRef.current?.()}
        mode="invoice"
      />

      <ConfirmModal
        isOpen={confirmModal.open}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmLabel={confirmModal.confirmLabel}
        destructive={confirmModal.destructive}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />

      {duplicateModal.open && (
        <div
          className="proposal-lock-overlay"
          onClick={() => setDuplicateModal({ open: false, inv: null, targetProjectId: '' })}
        >
          <div className="proposal-lock-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Duplicate Invoice</h3>
            <p>Create a copy of this invoice as a new draft. Choose which project it belongs to.</p>
            <div style={{ margin: '12px 0 20px' }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 6 }}>
                Project
              </label>
              <select
                value={duplicateModal.targetProjectId}
                onChange={(e) =>
                  setDuplicateModal((s) => ({ ...s, targetProjectId: e.target.value }))
                }
                style={{ width: '100%' }}
              >
                <option value={projectId}>
                  {(project?.projectNickname || project?.name || project?.customerName || 'This project')}{' '}
                  (current project)
                </option>
                {duplicateTargetProjects
                  .filter((p) => p.id !== projectId)
                  .map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
              </select>
            </div>
            <div className="proposal-lock-actions">
              <button
                className="if-btn-secondary"
                onClick={() => setDuplicateModal({ open: false, inv: null, targetProjectId: '' })}
              >
                Cancel
              </button>
              <button className="if-btn-primary" onClick={confirmDuplicateInvoice}>
                Duplicate
              </button>
            </div>
          </div>
        </div>
      )}

      {sendInvoiceModal.open && (
        <SendEmailModal
          type="invoice"
          defaultEmail={sendInvoiceModal.email}
          clientName={sendInvoiceModal.inv?.client?.name || ''}
          docNumber={sendInvoiceModal.inv?.invoiceNumber || ''}
          docUrl={sendInvoiceModal.publicUrl}
          companyName={sendInvoiceModal.inv?.companyName || project?.companyName || ''}
          onClose={() => setSendInvoiceModal({ open: false, inv: null, publicUrl: '', email: '' })}
          onSent={() => notify('Invoice sent to client.')}
        />
      )}

      {sendTBInvoiceModal.open && (
        <SendEmailModal
          type="invoice"
          defaultEmail={sendTBInvoiceModal.email}
          clientName={sendTBInvoiceModal.inv?.billToName || ''}
          docNumber={sendTBInvoiceModal.inv?.invoiceNumber || ''}
          docUrl={sendTBInvoiceModal.publicUrl}
          companyName={sendTBInvoiceModal.inv?.company?.name || ''}
          onClose={() => setSendTBInvoiceModal({ open: false, inv: null, publicUrl: '', email: '' })}
          onSent={() => notify('Invoice sent to client.')}
        />
      )}

      {itemSelectModal.open && (
        <div
          className="proposal-lock-overlay"
          onClick={() => setItemSelectModal({ open: false, invoice: null, items: [], selected: [] })}
        >
          <div className="proposal-lock-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Select items to convert</h3>
            <p>Choose which line items from this invoice should be included on the new purchase order.</p>
            <div style={{ marginBottom: 8 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={itemSelectModal.selected.length === itemSelectModal.items.length && itemSelectModal.items.length > 0}
                  onChange={toggleConvertAllItemsSelected}
                />
                Select All ({itemSelectModal.selected.length}/{itemSelectModal.items.length})
              </label>
            </div>
            <div style={{ maxHeight: 320, overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: 8, padding: '4px 10px' }}>
              {itemSelectModal.items.map((it) => (
                <label
                  key={it.itemId}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 8,
                    padding: '8px 0',
                    borderBottom: '1px solid #f1f5f9',
                    cursor: 'pointer',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={itemSelectModal.selected.includes(it.itemId)}
                    onChange={() => toggleConvertItemSelected(it.itemId)}
                    style={{ marginTop: 2 }}
                  />
                  <span style={{ flex: 1 }}>
                    <span style={{ display: 'block', fontWeight: 500 }}>{it.name || 'Untitled item'}</span>
                    <span style={{ display: 'block', fontSize: 12, color: '#6b7280' }}>
                      Qty {Number(it.qty || it.quantity || 0)} &middot; {formatCurrency(Number(it.tradePrice || it.rate || 0))}
                      {it.room ? ` · ${it.room}` : ''}
                    </span>
                  </span>
                </label>
              ))}
            </div>
            <div className="proposal-lock-actions">
              <button
                className="if-btn-secondary"
                onClick={() => setItemSelectModal({ open: false, invoice: null, items: [], selected: [] })}
              >
                Cancel
              </button>
              <button className="if-btn-primary" onClick={proceedFromItemSelection}>
                Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {vendorModal.open && (
        <div
          className="proposal-lock-overlay"
          onClick={() => setVendorModal({ open: false, invoice: null, vendors: [], itemIds: [] })}
        >
          <div className="proposal-lock-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Multiple vendors found</h3>
            <p>
              This invoice includes items from multiple vendors. Create one purchase order per
              vendor?
              {unassignedItemsNotice(vendorModal.unassignedCount)}
            </p>
            <div style={{ marginBottom: 10 }}>
              {vendorModal.vendors.map((v) => (
                <span
                  key={v}
                  style={{
                    display: 'inline-block',
                    padding: '4px 8px',
                    borderRadius: 999,
                    background: '#eef2ff',
                    color: '#1e1b4b',
                    fontSize: 12,
                    marginRight: 6,
                    marginBottom: 6,
                  }}
                >
                  {v}
                </span>
              ))}
            </div>
            <div className="proposal-lock-actions">
              <button
                className="if-btn-secondary"
                onClick={() => setVendorModal({ open: false, invoice: null, vendors: [], itemIds: [] })}
              >
                Cancel
              </button>
              <button className="if-btn-primary" onClick={confirmSplitVendors}>
                Create Purchase Orders
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

/* ---------------------- Status Badge ---------------------- */
function StatusBadge({ status }) {
  const colors = {
    draft: '#666',
    sent: '#1456d9',
    approved: '#0a7d33',
    changes_requested: '#b56a00',
    paid: '#0a7d33',
    partial: '#b56a00',
    partially_paid: '#b56a00',
    unpaid: '#6b7280',
  };
  const safeStatus = status || 'draft';
  const display = safeStatus === 'partial' ? 'Partially Paid' : safeStatus.replace(/_/g, ' ');
  return (
    <span
      style={{
        padding: '4px 8px',
        borderRadius: '12px',
        fontSize: '12px',
        color: colors[safeStatus] || '#333',
        background: `${colors[safeStatus] || '#ccc'}22`,
        textTransform: 'capitalize',
      }}
    >
      {display}
    </span>
  );
}

InvoicesSection.propTypes = {
  projectId: PropTypes.string,
  project: PropTypes.object,
};
